/**
 * App Mode Detection Utility
 * Detects if the application is running in DC-Photos mode based on hostname or URL parameter
 *
 * PROTOTYPING: Use ?mode=dc-photos in URL to test DC-Photos mode locally
 */

import { logger } from './logger.js';

/**
 * Detect if app is running in DC-Photos mode
 * Checks both hostname (for production) and URL parameter (for development/testing)
 * @returns {boolean}
 */
export function isDCPhotosMode() {
    // Check URL parameter for local testing (e.g., ?mode=dc-photos)
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode');
    if (modeParam === 'dc-photos') {
        return true;
    }

    // Check hostname for production (e.g., dc-photos.shuttersup.co.uk)
    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes('dc-photos');
}

/**
 * Get default file category based on app mode
 * @returns {string}
 */
export function getDefaultCategory() {
    return isDCPhotosMode() ? 'dc-photos' : '';
}

/**
 * Get app configuration based on hostname/mode
 * @returns {object}
 */
export function getAppConfig() {
    const isDCPhotos = isDCPhotosMode();

    return {
        mode: isDCPhotos ? 'dc-photos' : 'standard',
        defaultCategory: isDCPhotos ? 'dc-photos' : '',
        allowCategoryChange: true, // Always allow category change (best practice)
        multiFileUpload: true, // Always enabled
        appName: isDCPhotos ? 'DC-Photos' : 'Insightly Archive',

        // UI customization flags
        showNavigation: true, // ALWAYS show navigation (full app access)
        showUniversalSearch: true, // ALWAYS show universal search
        landOnOpportunities: isDCPhotos, // Start on opportunities page if DC-Photos
        customizeOpportunitiesView: isDCPhotos, // Apply DC-Photos styling to opportunities
        searchTitle: isDCPhotos ? 'Upload Photos to Opportunity' : null,
        searchDescription: isDCPhotos ? 'Search for an opportunity, then view and upload photos' : null,
        focusOnUpload: isDCPhotos // Emphasize upload UI in opportunities view
    };
}

/**
 * Apply DC-Photos styling and configuration if in DC-Photos mode
 */
export function applyAppMode() {
    const config = getAppConfig();

    if (config.mode === 'dc-photos') {
    // Add CSS class for styling
        document.body.classList.add('dc-photos-mode');

        // Update page title
        document.title = 'DC-Photos Upload';

        // Log for debugging
        logger.debug('🎨 DC-Photos mode activated');
        logger.debug('Mode config:', config);
    } else {
    // Ensure standard mode
        document.body.classList.remove('dc-photos-mode');
        document.title = 'Insightly Archive';
        logger.debug('📁 Standard mode active');
    }

    return config;
}

/**
 * Get URL for current mode (helper for testing)
 * @param {string} mode - 'dc-photos' or 'standard'
 * @returns {string} URL with mode parameter
 */
export function getModeUrl(mode = 'standard') {
    const url = new URL(window.location.href);

    if (mode === 'dc-photos') {
        url.searchParams.set('mode', 'dc-photos');
    } else {
        url.searchParams.delete('mode');
    }

    return url.toString();
}

/**
 * Add mode switcher for development/testing
 * Shows a floating button to toggle between modes
 */
export function addModeSwitcher() {
    // Only show in development
    if (!window.location.hostname.includes('localhost') &&
      !window.location.hostname.includes('127.0.0.1')) {
        return;
    }

    const isDCPhotos = isDCPhotosMode();

    const switcher = document.createElement('div');
    switcher.id = 'mode-switcher';
    switcher.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 10000;
    padding: 10px 15px;
    background: ${isDCPhotos ? '#ff6b35' : '#4CAF50'};
    color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    cursor: pointer;
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: bold;
    transition: all 0.3s;
  `;

    switcher.innerHTML = `
    <div>🎨 Mode: ${isDCPhotos ? 'DC-Photos' : 'Standard'}</div>
    <div style="font-size: 11px; opacity: 0.9;">Click to switch</div>
  `;

    switcher.addEventListener('click', () => {
        const newMode = isDCPhotos ? 'standard' : 'dc-photos';
        window.location.href = getModeUrl(newMode);
    });

    switcher.addEventListener('mouseenter', () => {
        switcher.style.transform = 'scale(1.05)';
    });

    switcher.addEventListener('mouseleave', () => {
        switcher.style.transform = 'scale(1)';
    });

    document.body.appendChild(switcher);
    logger.debug('🔧 Mode switcher added (dev only)');
}
